import * as React from 'react';
import * as classNames from 'classnames';
import { Overlay as BlueprintOverlay } from '@blueprintjs/core';

import './styles.scss';
import Button, { IButtonProps } from '../Button';
import Buttons from '../Buttons';

export interface IOverlayProps {
    /** Layouts to choose from */
    layout: 'dialog' | 'mainmenu' | 'focusview' | 'tabledetails';
    /** Transition between open and closed state */
    isOpen: boolean;
    /** Triggered when closing transition starts */
    onClose?: (event?) => void;
    /** Triggered when transition ends */
    onClosed?: () => void;
    /** Closing function to initiate closing */
    doClose: (event?) => void;
    /** Overlay title for focusview */
    title?: string;
    /** zIndex stack order */
    zIndex?: number;
    /** className to append */
    className?: string;
    /** Disable scrolling */
    disableScrolling?: boolean;
    /** Dialog config */
    dialogConfig?: {
        /** Optional description to display */
        description?: React.ReactNode;
        /** Buttons to display */
        buttons?: IButtonProps[];
        /** Layout of buttons */
        layout?: 'default' | 'reversed' | 'centered' | 'vertical' | 'vertical-wide' | 'sticky';
    };
    /** Render custom header above scrollable area */
    customHeader?: React.ReactNode;
}

interface IState {
}

/**
 * Overlay handler based on Blueprint overlay. Combine this with OverlayHOC and react-router to have URL/Hash based functionality.
 */
class Overlay extends React.Component<IOverlayProps, IState> {
    static displayName: string = 'Overlay';

    private nodeScroller;
    private cachedClientY = 0;
    private preventTouchBubbling = this.handlePreventTouchBubbling.bind(this);
    private cacheClientY = this.handleCacheClientY.bind(this);

    handlePreventTouchBubbling (event) {
        if (event.targetTouches.length !== 1) {
            return;
        }

        const clientY = event.targetTouches[0].clientY - this.cachedClientY;

        if (this.nodeScroller.scrollTop === 0 && clientY > 0) {
            event.preventDefault();
        }

        if ((this.nodeScroller.scrollHeight - this.nodeScroller.scrollTop <= this.nodeScroller.clientHeight) && clientY < 0) {
            event.preventDefault();
        }

    }

    handleCacheClientY (event) {
        if (event.targetTouches.length !== 1) {
            return;
        }
        this.cachedClientY = event.targetTouches[0].clientY;
    }

    render () {
        let z = 20;
        if (this.props.zIndex) {
            z = 20 * (this.props.zIndex + 1);
        } else if (this.props.layout === 'dialog') {
            z = 50;
        }

        return (
            <BlueprintOverlay
                isOpen={this.props.isOpen}
                onClose={this.props.onClose}
                onClosed={() => {
                    if (this.props.onClosed) {
                        this.props.onClosed();
                    }
                    window.dispatchEvent(new CustomEvent('lqa:overlay:closed'));
                }}
                onOpened={() => {
                    // For updating all popper instances, which use getBoundingClientRect and get wrong positions due to css transitions.
                    window.dispatchEvent(new CustomEvent('resize'));
                    window.dispatchEvent(new CustomEvent('lqa:overlay:opened'));
                }}
                onOpening={() => {
                    window.dispatchEvent(new CustomEvent('resize'));
                    if (this.nodeScroller) {
                        this.nodeScroller.scrollTop = 0;
                    }
                }}
                transitionDuration={250}
                canOutsideClickClose={false}
                hasBackdrop={false}
                lazy={true}
                usePortal={true}
                canEscapeKeyClose={false}
                enforceFocus={false}
            >
                <div className={classNames('overlayholder', 'overlayholder-' + this.props.layout)} style={{ zIndex: z, }}>
                    <div className={classNames('overlay-' + this.props.layout, this.props.className)}>
                        <div className={'overlay-' + this.props.layout + '-holder'}>
                            <div className={'overlay-' + this.props.layout + '-content'}>
                                {this.props.customHeader}
                                <div
                                    className={this.props.disableScrolling ? 'overlay-' + this.props.layout + '-noscroll' : 'overlay-' + this.props.layout + '-scroller'}
                                    ref={(node) => {
                                        if (node) {
                                            this.nodeScroller = node;
                                            if (!this.props.disableScrolling) {
                                                node.addEventListener('touchstart', this.cacheClientY, { passive: false });
                                                node.addEventListener('touchmove', this.preventTouchBubbling, { passive: false });
                                            }
                                        }
                                    }}
                                >
                                    {this.props.layout === 'dialog' && this.props.dialogConfig ? (
                                        <React.Fragment>
                                            {!!this.props.title && <h2 className={'overlay-' + this.props.layout + '-title'}>{this.props.title}</h2>}
                                            {!!this.props.dialogConfig.description && (
                                                <p className={this.props.dialogConfig.layout?"":"overlay-dialog-description"}>{this.props.dialogConfig.description}</p>
                                            )}
                                            {this.props.dialogConfig.buttons && !!this.props.dialogConfig.buttons.length && (
                                                <Buttons layout={this.props.dialogConfig.layout || "centered"}>
                                                    {this.props.dialogConfig.buttons.map((b, i) => (
                                                        <Button
                                                            ref={(node) => {
                                                                if (i === 0 && node) {
                                                                    node.focus();
                                                                }
                                                            }}
                                                            key={i}
                                                            {...b}
                                                            onClick={(e) => {
                                                                this.props.doClose(e);
                                                                if (b.onClick) {
                                                                    b.onClick(e);
                                                                }
                                                            }}
                                                        />
                                                    ))}
                                                </Buttons>
                                            )}
                                        </React.Fragment>
                                    ) : (
                                        this.props.children
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {this.props.layout === 'dialog' && (
                    <span
                        className="overlayholder-backdrop"
                        style={{ zIndex: z - 1, }}
                    />
                )}
            </BlueprintOverlay>
        );
    }
}

export default Overlay;
